import { useCallback, useEffect, useMemo, useState } from 'react';
import { IconPin } from '../components/Icons';
import { Link, useSearchParams } from 'react-router-dom';
import { toApiError, type ApiError } from '../api/client';
import { meApi, propertyApi, referenceApi, searchApi } from '../api/endpoints';
import type { AccommodationCategory, Amenity, Locality, PropertySearchResult } from '../api/types';
import { useAuth } from '../auth/AuthContext';
import { ChipGroup } from '../components/Common';
import { Pagination } from '../components/Common';
import { ResultsMap } from '../components/MapView';
import { PropertyCard } from '../components/PropertyCard';
import { LocationAutocomplete } from '../components/LocationAutocomplete';
import { EmptyState, ErrorState, InlineError, ListingGridSkeleton } from '../components/States';
import { categoryLabel } from '../utils/format';

type View = 'list' | 'map';

interface Props {
  category: AccommodationCategory;
}

const SORT_OPTIONS = [
  { value: 'relevance', label: 'Most relevant' },
  { value: 'price_asc', label: 'Price: low to high' },
  { value: 'price_desc', label: 'Price: high to low' },
  { value: 'newest', label: 'Newest first' },
  { value: 'distance', label: 'Nearest first' },
];

export function SearchResultsPage({ category }: Props) {
  const [params, setParams] = useSearchParams();
  const { isAuthenticated } = useAuth();

  const [data, setData] = useState<PropertySearchResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  // Separate from `error`: the search itself succeeded, only the browser's location
  // prompt did not, so this must not replace the results with a failure screen.
  const [locationNotice, setLocationNotice] = useState<string | null>(null);
  const [view, setView] = useState<View>('list');
  const [amenities, setAmenities] = useState<Amenity[]>([]);
  const [localities, setLocalities] = useState<Locality[]>([]);
  const [locationInput, setLocationInput] = useState('');
  const [savedIds, setSavedIds] = useState<Set<string>>(new Set());
  const [filtersOpen, setFiltersOpen] = useState(false);

  // The URL is the single source of truth for the search, so results are shareable and
  // the back button behaves.
  const get = useCallback((key: string) => params.get(key) ?? undefined, [params]);
  const getAll = useCallback((key: string) => params.getAll(key), [params]);
  const getNumber = useCallback(
    (key: string) => {
      const raw = params.get(key);
      return raw ? Number(raw) : undefined;
    },
    [params],
  );

  const update = useCallback(
    (changes: Record<string, string | string[] | undefined>, resetPage = true) => {
      const next = new URLSearchParams(params);

      for (const [key, value] of Object.entries(changes)) {
        next.delete(key);

        if (Array.isArray(value)) {
          value.forEach((v) => next.append(key, v));
        } else if (value !== undefined && value !== '') {
          next.set(key, value);
        }
      }

      if (resetPage) next.delete('page');

      setParams(next, { replace: false });
    },
    [params, setParams],
  );

  useEffect(() => {
    setLocationInput(get('locality') ?? '');
  }, [get]);

  useEffect(() => {
    referenceApi.amenities(category, true).then(setAmenities).catch(() => setAmenities([]));
    referenceApi.localities(undefined, category).then(setLocalities).catch(() => setLocalities([]));
  }, [category]);

  useEffect(() => {
    if (!isAuthenticated) {
      setSavedIds(new Set());
      return;
    }

    meApi.favouriteIds().then((ids) => setSavedIds(new Set(ids))).catch(() => setSavedIds(new Set()));
  }, [isAuthenticated]);

  const query = useMemo(() => {
    const base = {
      locality: get('locality'),
      keyword: get('keyword'),
      minPrice: getNumber('minPrice'),
      maxPrice: getNumber('maxPrice'),
      latitude: getNumber('latitude'),
      longitude: getNumber('longitude'),
      radiusKm: getNumber('radiusKm'),
      amenities: getAll('amenities'),
      sort: get('sort') ?? 'relevance',
      page: getNumber('page') ?? 1,
      pageSize: 12,
    };

    if (category === 'PgHostel') {
      return {
        ...base,
        gender: get('gender'),
        sharingTypes: getAll('sharingTypes'),
        foodProvided: get('foodProvided') === 'true' ? true : undefined,
        airConditioned: get('airConditioned') === 'true' ? true : undefined,
        attachedBathroom: get('attachedBathroom') === 'true' ? true : undefined,
        minAvailableBeds: getNumber('minAvailableBeds'),
      };
    }

    if (category === 'HomeRental') {
      return {
        ...base,
        bedrooms: getAll('bedrooms').map(Number),
        homeTypes: getAll('homeTypes'),
        furnishing: getAll('furnishing'),
        tenantPreferences: getAll('tenantPreferences'),
        hasCarParking: get('hasCarParking') === 'true' ? true : undefined,
        availableBy: get('availableBy'),
      };
    }

    return {
      ...base,
      hotelTypes: getAll('hotelTypes'),
      roomCategories: getAll('roomCategories'),
      guests: getNumber('guests'),
      airConditioned: get('airConditioned') === 'true' ? true : undefined,
      minStarRating: getNumber('minStarRating'),
      checkIn: get('checkIn'),
      checkOut: get('checkOut'),
    };
  }, [category, get, getAll, getNumber]);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const request =
        category === 'PgHostel'
          ? searchApi.pg(query)
          : category === 'HomeRental'
            ? searchApi.homes(query)
            : searchApi.hotels(query);

      setData(await request);
    } catch (err) {
      setError(toApiError(err));
    } finally {
      setLoading(false);
    }
  }, [category, query]);

  useEffect(() => {
    void load();
  }, [load]);

  const toggleSave = async (propertyId: string) => {
    if (!isAuthenticated) {
      window.location.href = `/login?returnTo=${encodeURIComponent(window.location.pathname + window.location.search)}`;
      return;
    }

    try {
      const saved = await propertyApi.toggleFavourite(propertyId);

      setSavedIds((previous) => {
        const next = new Set(previous);
        if (saved) next.add(propertyId);
        else next.delete(propertyId);
        return next;
      });
    } catch {
      // A failed save should not disturb the results the person is reading.
    }
  };

  const useMyLocation = () => {
    navigator.geolocation?.getCurrentPosition(
      (position) =>
        update({
          latitude: position.coords.latitude.toFixed(6),
          longitude: position.coords.longitude.toFixed(6),
          radiusKm: params.get('radiusKm') ?? '5',
          sort: 'distance',
        }),
      () => setLocationNotice('We could not get your location. Search by locality instead.'),
      { timeout: 10000 },
    );
  };

  const clearLocation = () => update({ latitude: undefined, longitude: undefined, radiusKm: undefined, sort: 'relevance' });

  const activeFilterCount = [...params.keys()].filter(
    (key) => !['page', 'sort'].includes(key),
  ).length;

  const searchPath: Record<AccommodationCategory, string> = {
    PgHostel: '/pg',
    HomeRental: '/homes',
    Hotel: '/hotels',
  };
  const categoryTabs: { value: AccommodationCategory; label: string }[] = [
    { value: 'PgHostel', label: 'PG & Hostels' },
    { value: 'HomeRental', label: 'Homes' },
    { value: 'Hotel', label: 'Hotels' },
  ];
  const activeCategoryLabel = categoryTabs.find((tab) => tab.value === category)?.label ?? categoryLabel(category);

  return (
    <div className="sf-search-page container-fluid">
      <section className="sf-search-hero mb-4" aria-labelledby="search-title">
        <div className="sf-search-hero__intro">
          <span className="sf-search-hero__eyebrow">ROOMKART</span>
          <h1 id="search-title">Find your perfect stay</h1>
          <p>PGs, hostels, homes and hotels — all in one place.</p>
        </div>
        <div className="sf-search-hero__search">
          <nav className="sf-search-tabs" aria-label="Property type">
            {categoryTabs.map((tab) => (
              <Link
                key={tab.value}
                to={searchPath[tab.value]}
                className={`sf-search-tabs__link${category === tab.value ? ' is-active' : ''}`}
                aria-current={category === tab.value ? 'page' : undefined}
              >
                {tab.label}
              </Link>
            ))}
          </nav>
          <form
            className="sf-search-hero__form"
            onSubmit={(event) => {
              event.preventDefault();
              update({ locality: locationInput.trim() || undefined });
            }}
          >
            <label className="sf-search-hero__location">
              <IconPin />
              <span className="visually-hidden">Locality or city</span>
              <LocationAutocomplete
                id="results-locality"
                className="form-control"
                value={locationInput}
                options={localities}
                placeholder="Where are you looking?"
                onChange={setLocationInput}
                onCommit={(value) => update({ locality: value || undefined })}
              />
            </label>
            <button type="submit" className="btn btn-primary sf-search-hero__submit">
              Search
            </button>
          </form>
        </div>
      </section>

      <div className="sf-search-heading d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <div>
          <div className="sf-search-breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">›</span>
            <span>{activeCategoryLabel}</span>
          </div>
          <h2 className="h3 mb-1">
            {activeCategoryLabel}
            {get('locality') ? ` in ${get('locality')}` : ''}
          </h2>
          <p className="sf-muted mb-0" aria-live="polite">
            {loading
              ? 'Searching…'
              : data
                ? `${data.results.totalCount.toLocaleString()} ${data.results.totalCount === 1 ? 'property' : 'properties'} found`
                : ''}
          </p>
        </div>

        <div className="d-flex align-items-center gap-2">
          <button
            type="button"
            className="btn btn-outline-secondary d-lg-none"
            onClick={() => setFiltersOpen((open) => !open)}
            aria-expanded={filtersOpen}
          >
            Filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
          </button>

          <div className="btn-group sf-view-toggle" role="group" aria-label="View mode">
            <button
              type="button"
              className={`btn ${view === 'list' ? 'btn-primary' : 'btn-outline-secondary'}`}
              aria-pressed={view === 'list'}
              onClick={() => setView('list')}
            >
              List
            </button>
            <button
              type="button"
              className={`btn ${view === 'map' ? 'btn-primary' : 'btn-outline-secondary'}`}
              aria-pressed={view === 'map'}
              onClick={() => setView('map')}
            >
              Map
            </button>
          </div>
        </div>
      </div>

      {/* Anything the server could not honour is stated plainly rather than silently
          ignored - for example a radius with no location, or hotel stay dates. */}
      {data?.notices.map((notice) => (
        <div className="alert alert-info py-2 px-3" role="status" key={notice}>
          {notice}
        </div>
      ))}

      <div className="row g-3">
        <aside className={`col-12 col-lg-3 ${filtersOpen ? '' : 'd-none d-lg-block'}`}>
          <div className="sf-filter-panel">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h2 className="h6 mb-0">Filters</h2>
              {activeFilterCount > 0 && (
                <button type="button" className="btn btn-sm btn-link px-0" onClick={() => setParams(new URLSearchParams())}>
                  Clear all
                </button>
              )}
            </div>

            <div className="sf-filter-group">
              <h4>Location</h4>
              <LocationAutocomplete
                id="search-locality"
                className="form-control form-control-sm mb-2"
                value={locationInput}
                options={localities}
                placeholder="Locality or city"
                onChange={setLocationInput}
                onCommit={(value) => update({ locality: value || undefined })}
              />

              {get('latitude') ? (
                <div className="d-flex flex-column gap-2">
                  <label className="form-label mb-0 small" htmlFor="radius">
                    Within {get('radiusKm') ?? 5} km of you
                  </label>
                  <input
                    id="radius"
                    type="range"
                    className="form-range"
                    min={1}
                    max={25}
                    step={1}
                    defaultValue={Number(get('radiusKm') ?? 5)}
                    onChange={(e) => update({ radiusKm: e.target.value })}
                  />
                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={clearLocation}>
                    Clear location
                  </button>
                </div>
              ) : (
                <button type="button" className="btn btn-sm btn-outline-primary w-100" onClick={useMyLocation}>
                  <IconPin /> Search near me
                </button>
              )}
            </div>

            <div className="sf-filter-group">
              <h4>Budget ({category === 'Hotel' ? 'per night' : 'per month'})</h4>
              <div className="d-flex gap-2">
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  placeholder="Min"
                  aria-label="Minimum price"
                  defaultValue={get('minPrice') ?? ''}
                  onBlur={(e) => update({ minPrice: e.target.value || undefined })}
                />
                <input
                  type="number"
                  min={0}
                  className="form-control form-control-sm"
                  placeholder="Max"
                  aria-label="Maximum price"
                  defaultValue={get('maxPrice') ?? ''}
                  onBlur={(e) => update({ maxPrice: e.target.value || undefined })}
                />
              </div>
            </div>

            {category === 'PgHostel' && <PgFilters get={get} getAll={getAll} update={update} />}
            {category === 'HomeRental' && <HomeFilters get={get} getAll={getAll} update={update} />}
            {category === 'Hotel' && <HotelFilters get={get} getAll={getAll} update={update} />}

            {amenities.length > 0 && (
              <div className="sf-filter-group">
                <h4>Amenities</h4>
                <ChipGroup
                  legend="Amenities"
                  options={amenities.map((a) => ({ value: a.code, label: a.name }))}
                  selected={getAll('amenities')}
                  onChange={(values) => update({ amenities: values })}
                />
              </div>
            )}
          </div>
        </aside>

        <div className="col-12 col-lg-9">
          <div className="d-flex justify-content-end mb-2">
            <label className="form-label mb-0 me-2 align-self-center small" htmlFor="sort">
              Sort
            </label>
            <select
              id="sort"
              className="form-select form-select-sm"
              style={{ width: 'auto' }}
              value={get('sort') ?? 'relevance'}
              onChange={(e) => update({ sort: e.target.value }, false)}
            >
              {SORT_OPTIONS.filter((o) => o.value !== 'distance' || data?.distanceAvailable).map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>

          {loading && <ListingGridSkeleton count={6} />}

          {locationNotice && <InlineError message={locationNotice} />}
          {!loading && error && <ErrorState error={error} onRetry={load} />}

          {!loading && !error && data && data.results.items.length === 0 && (
            <EmptyState
              title="No properties match these filters"
              description="Try widening your budget, removing a filter, or searching a nearby locality."
              action={
                <button type="button" className="btn btn-outline-primary" onClick={() => setParams(new URLSearchParams())}>
                  Clear all filters
                </button>
              }
            />
          )}

          {!loading && !error && data && data.results.items.length > 0 && (
            <>
              {view === 'map' ? (
                <ResultsMap properties={data.results.items} />
              ) : (
                <div className="sf-search-results">
                  {data.results.items.map((property) => (
                    <PropertyCard
                      key={property.id}
                      property={property}
                      isSaved={savedIds.has(property.id)}
                      onToggleSave={toggleSave}
                      showDistance={data.distanceAvailable}
                    />
                  ))}
                </div>
              )}

              <Pagination result={data.results} onPageChange={(page) => update({ page: String(page) }, false)} />
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// =====================================================================================
// Category-specific filter panels
// =====================================================================================

interface FilterProps {
  get: (key: string) => string | undefined;
  getAll: (key: string) => string[];
  update: (changes: Record<string, string | string[] | undefined>, resetPage?: boolean) => void;
}

function PgFilters({ get, getAll, update }: FilterProps) {
  return (
    <>
      <div className="sf-filter-group">
        <h4>Accommodation for</h4>
        <ChipGroup
          legend="Gender"
          multiple={false}
          options={[
            { value: 'Boys', label: 'Men' },
            { value: 'Girls', label: 'Women' },
            { value: 'Unisex', label: 'Unisex' },
          ]}
          selected={get('gender') ? [get('gender') as string] : []}
          onChange={(values) => update({ gender: values[0] })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Sharing</h4>
        <ChipGroup
          legend="Sharing type"
          options={[
            { value: 'Single', label: 'Single' },
            { value: 'TwoSharing', label: '2 sharing' },
            { value: 'ThreeSharing', label: '3 sharing' },
            { value: 'FourSharing', label: '4 sharing' },
            { value: 'FiveSharing', label: '5 sharing' },
            { value: 'SixSharing', label: '6 sharing' },
            { value: 'SevenSharing', label: '7 sharing' },
            { value: 'EightSharing', label: '8 sharing' },
          ]}
          selected={getAll('sharingTypes')}
          onChange={(values) => update({ sharingTypes: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Room and food</h4>
        <ToggleFilter label="Food included" param="foodProvided" get={get} update={update} />
        <ToggleFilter label="Air conditioned" param="airConditioned" get={get} update={update} />
        <ToggleFilter label="Attached bathroom" param="attachedBathroom" get={get} update={update} />
      </div>

      <div className="sf-filter-group">
        <h4>Availability</h4>
        <label className="form-label small" htmlFor="min-beds">
          At least this many beds free
        </label>
        <input
          id="min-beds"
          type="number"
          min={1}
          className="form-control form-control-sm"
          defaultValue={get('minAvailableBeds') ?? ''}
          onBlur={(e) => update({ minAvailableBeds: e.target.value || undefined })}
        />
        <div className="form-text">Only matches listings whose availability is confirmed.</div>
      </div>
    </>
  );
}

function HomeFilters({ get, getAll, update }: FilterProps) {
  return (
    <>
      <div className="sf-filter-group">
        <h4>Bedrooms</h4>
        <ChipGroup
          legend="Bedrooms"
          options={[1, 2, 3, 4, 5].map((n) => ({ value: String(n), label: `${n} BHK` }))}
          selected={getAll('bedrooms')}
          onChange={(values) => update({ bedrooms: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Property type</h4>
        <ChipGroup
          legend="Home type"
          options={[
            { value: 'Apartment', label: 'Apartment' },
            { value: 'IndependentHouse', label: 'Independent house' },
            { value: 'Villa', label: 'Villa' },
            { value: 'StudioApartment', label: 'Studio' },
          ]}
          selected={getAll('homeTypes')}
          onChange={(values) => update({ homeTypes: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Furnishing</h4>
        <ChipGroup
          legend="Furnishing"
          options={[
            { value: 'Unfurnished', label: 'Unfurnished' },
            { value: 'SemiFurnished', label: 'Semi furnished' },
            { value: 'FullyFurnished', label: 'Fully furnished' },
          ]}
          selected={getAll('furnishing')}
          onChange={(values) => update({ furnishing: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Tenant preference</h4>
        <ChipGroup
          legend="Tenant preference"
          options={[
            { value: 'FAMILY', label: 'Family' },
            { value: 'WORKING_PROFESSIONALS', label: 'Working professionals' },
            { value: 'STUDENTS', label: 'Students' },
            { value: 'BACHELORS', label: 'Bachelors' },
            { value: 'COMPANY_LEASE', label: 'Company lease' },
          ]}
          selected={getAll('tenantPreferences')}
          onChange={(values) => update({ tenantPreferences: values })}
        />
        <div className="form-text">Shows homes the owner has opened to these tenants, plus any with no restriction.</div>
      </div>

      <div className="sf-filter-group">
        <h4>Other</h4>
        <ToggleFilter label="Car parking" param="hasCarParking" get={get} update={update} />

        <label className="form-label small mt-2" htmlFor="available-by">
          Available by
        </label>
        <input
          id="available-by"
          type="date"
          className="form-control form-control-sm"
          defaultValue={get('availableBy') ?? ''}
          onChange={(e) => update({ availableBy: e.target.value || undefined })}
        />
      </div>
    </>
  );
}

function HotelFilters({ get, getAll, update }: FilterProps) {
  return (
    <>
      <div className="sf-filter-group">
        <h4>Your stay</h4>
        <div className="row g-2">
          <div className="col-6">
            <label className="form-label small" htmlFor="check-in">
              Check in
            </label>
            <input
              id="check-in"
              type="date"
              className="form-control form-control-sm"
              defaultValue={get('checkIn') ?? ''}
              onChange={(e) => update({ checkIn: e.target.value || undefined })}
            />
          </div>
          <div className="col-6">
            <label className="form-label small" htmlFor="check-out">
              Check out
            </label>
            <input
              id="check-out"
              type="date"
              className="form-control form-control-sm"
              defaultValue={get('checkOut') ?? ''}
              onChange={(e) => update({ checkOut: e.target.value || undefined })}
            />
          </div>
        </div>
        <div className="form-text">
          Dates are passed on with your enquiry. RoomKart does not hold live date availability yet.
        </div>

        <label className="form-label small mt-2" htmlFor="guests">
          Guests
        </label>
        <input
          id="guests"
          type="number"
          min={1}
          max={20}
          className="form-control form-control-sm"
          defaultValue={get('guests') ?? ''}
          onBlur={(e) => update({ guests: e.target.value || undefined })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Hotel type</h4>
        <ChipGroup
          legend="Hotel type"
          options={[
            { value: 'Budget', label: 'Budget' },
            { value: 'Business', label: 'Business' },
            { value: 'Premium', label: 'Premium' },
            { value: 'Resort', label: 'Resort' },
            { value: 'ServiceApartment', label: 'Service apartment' },
          ]}
          selected={getAll('hotelTypes')}
          onChange={(values) => update({ hotelTypes: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Room type</h4>
        <ChipGroup
          legend="Room category"
          options={[
            { value: 'Single', label: 'Single' },
            { value: 'Double', label: 'Double' },
            { value: 'TwinBed', label: 'Twin bed' },
            { value: 'Deluxe', label: 'Deluxe' },
            { value: 'Suite', label: 'Suite' },
            { value: 'Family', label: 'Family' },
          ]}
          selected={getAll('roomCategories')}
          onChange={(values) => update({ roomCategories: values })}
        />
      </div>

      <div className="sf-filter-group">
        <h4>Other</h4>
        <ToggleFilter label="Air conditioned" param="airConditioned" get={get} update={update} />

        <label className="form-label small mt-2" htmlFor="stars">
          Minimum star rating
        </label>
        <select
          id="stars"
          className="form-select form-select-sm"
          value={get('minStarRating') ?? ''}
          onChange={(e) => update({ minStarRating: e.target.value || undefined })}
        >
          <option value="">Any</option>
          <option value="3">3 stars and above</option>
          <option value="4">4 stars and above</option>
          <option value="5">5 stars</option>
        </select>
        <div className="form-text">Ratings are labelled as owner-stated unless RoomKart has verified them.</div>
      </div>
    </>
  );
}

function ToggleFilter({
  label,
  param,
  get,
  update,
}: {
  label: string;
  param: string;
  get: (key: string) => string | undefined;
  update: FilterProps['update'];
}) {
  const checked = get(param) === 'true';

  return (
    <div className="form-check">
      <input
        className="form-check-input"
        type="checkbox"
        id={`filter-${param}`}
        checked={checked}
        onChange={(e) => update({ [param]: e.target.checked ? 'true' : undefined })}
      />
      <label className="form-check-label" htmlFor={`filter-${param}`}>
        {label}
      </label>
    </div>
  );
}
